import * as React from 'react';
import TreeView from '@mui/lab/TreeView';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TreeItem from '@mui/lab/TreeItem';
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { CustomCheckbox } from '../CustomCheckbox';
import { GetFullTreeSummary, GetZoneSummaryLevel } from '../../reduxToolkit/EmployeeContractors/EmployeeContractorsApi';

/**
 * CustomTreeView renders a lazy-loaded zone tree with checkboxes.
 *
 * The tree is loaded one level at a time: the root level is fetched on mount and
 * each node's children are fetched on expand (only when `hasChildren` is true).
 * "Select all" fetches the full nested tree once so every descendant can be checked.
 *
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 * @param {Object} props - Component props.
 * @param {string} props.tittle - Title to display in the header (translation key).
 * @param {Function} props.onInputchange - Function triggered when a checkbox state changes.
 * @param {Array} props.isChecked - Array with selected values in the format "id,name".
 * @param {Array} props.checked - Array with currently checked values.
 * @param {Function} props.setChecked - Function to update the checked state.
 * @returns {JSX.Element} Rendered component.
 */
const CustomTreeView = ({ tittle = '', onInputchange, isChecked, checked, setChecked, className = "h-250px" }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [rootNodes, setRootNodes] = useState([]);
    // Children fetched per parent, keyed by String(parentId).
    const [childrenMap, setChildrenMap] = useState({});
    // In-flight fetch flags, keyed by String(parentId), to avoid duplicate requests.
    const [loadingMap, setLoadingMap] = useState({});
    const [expanded, setExpanded] = useState([]);
    const [isAllChecked, setIsAllChecked] = useState(false);
    // Once the full tree is fetched (select all) every node already carries its
    // `children`, so per-level lazy loading is no longer needed.
    const [fullTreeLoaded, setFullTreeLoaded] = useState(false);

    // Extracts the payload array from a thunk result ({ data: { data: [...] } }).
    const extractList = (res) => res?.payload?.data?.data ?? [];

    // Load the root level once on mount.
    useEffect(() => {
        let active = true;
        dispatch(GetZoneSummaryLevel()).then((res) => {
            if (active) setRootNodes(extractList(res));
        });
        return () => { active = false; };
    }, [dispatch]);

    /**
     * Lazy-loads the children of a node the first time it is expanded.
     *
     * @param {string|number} nodeId - Id of the parent node being expanded.
     */
    const loadChildren = (nodeId) => {
        // The full tree already holds every level; skip per-level requests.
        if (fullTreeLoaded) return;
        const key = String(nodeId);
        if (childrenMap[key] || loadingMap[key]) return;

        setLoadingMap((prev) => ({ ...prev, [key]: true }));
        dispatch(GetZoneSummaryLevel(nodeId)).then((res) => {
            setChildrenMap((prev) => ({ ...prev, [key]: extractList(res) }));
            setLoadingMap((prev) => ({ ...prev, [key]: false }));
        });
    };

    /**
     * Keeps the expanded state controlled and triggers lazy loading for newly
     * expanded nodes.
     *
     * @param {React.SyntheticEvent} _event - Toggle event.
     * @param {Array<string>} nodeIds - Ids of the currently expanded nodes.
     */
    const handleNodeToggle = (_event, nodeIds) => {
        const newlyExpanded = nodeIds.filter((id) => !expanded.includes(id));
        newlyExpanded.forEach((id) => loadChildren(id));
        setExpanded(nodeIds);
    };

    /**
     * Recursively collects the ids of all nodes in a nested tree.
     *
     * @param {Array} nodes - Array of tree nodes (with optional `children`).
     * @returns {Array<string>} Array of ids in the format "id,name".
     */
    const getAllItemIds = (nodes) => {
        return nodes.reduce((acc, node) => {
            acc.push(`${node.id},${node.name}`);
            if (node.children && node.children.length > 0) {
                acc = acc.concat(getAllItemIds(node.children));
            }
            return acc;
        }, []);
    };

    /**
     * Handles the "select all" checkbox. Checking fetches the full nested tree so
     * every descendant id can be selected; unchecking clears the selection.
     *
     * @param {React.ChangeEvent<HTMLInputElement>} e - The "select all" checkbox event.
     */
    const handleZoneCheckAll = (e) => {
        if (e.target.checked) {
            dispatch(GetFullTreeSummary()).then((res) => {
                const tree = extractList(res);
                // Reuse the fully-formed tree for rendering so expanding a node no
                // longer triggers a per-level fetch.
                setRootNodes(tree);
                setFullTreeLoaded(true);
                setChecked(getAllItemIds(tree));
                setIsAllChecked(true);
            });
        } else {
            setChecked([]);
            setIsAllChecked(false);
        }
    };

    /**
     * Recursively renders TreeItem components. Nodes flagged with `hasChildren`
     * render their loaded children, or a disabled loading stub so the expand arrow
     * is shown before the children are fetched.
     *
     * @param {Array} nodes - Array of nodes to render.
     * @returns {JSX.Element[]} Array of TreeItem components.
     */
    const renderTreeItems = (nodes) => {
        return nodes.map((node) => {
            const key = String(node.id);
            // After a full-tree load children live on the node; otherwise use the
            // per-level cache populated on expand.
            const loadedChildren = fullTreeLoaded ? (node.children ?? []) : childrenMap[key];

            return (
                <TreeItem
                    key={node.id}
                    nodeId={key}
                    label={
                        <React.Fragment>
                            <CustomCheckbox
                                id={`${node.id}`}
                                trans={node.name}
                                name={"selectAll"}
                                checked={isChecked.includes(`${node.id},${node.name}`)}
                                onChange={(event) => {
                                    event.stopPropagation();
                                    onInputchange({ target: { value: `${node.id},${node.name}`, checked: event.target.checked } });
                                }}
                                marginbutton={true}
                            />
                        </React.Fragment>
                    }
                >
                    {node.hasChildren && (
                        loadedChildren
                            ? renderTreeItems(loadedChildren)
                            : <TreeItem nodeId={`${key}-loading`} label={t('loading')} disabled />
                    )}
                </TreeItem>
            );
        });
    };

    return (
        <>
            <div className='d-flex align-items-center'>
                {tittle !== '' && <p className='heading_tree'>{t(tittle)}</p>}
                <CustomCheckbox id={"cb_ctv_de_select_all"}
                                trans={t("de_/_select_all")}
                                name={"selectAllDays"}
                                checked={isAllChecked && checked?.length > 0}
                                onChange={handleZoneCheckAll}
                                marginbutton={true}
                                className={"ml-2"}
                />
            </div>
            <div className={`overflow-y-auto maxh-412px ${className}`}>
                <TreeView
                    aria-label='file system navigator'
                    defaultCollapseIcon={<ExpandMoreIcon />}
                    defaultExpandIcon={<ChevronRightIcon />}
                    expanded={expanded}
                    onNodeToggle={handleNodeToggle}
                >
                    {renderTreeItems(rootNodes)}
                </TreeView>
            </div>
        </>
    );
};

export default CustomTreeView;
